import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

// NEXIS Assist — floating entry point into the existing /nexis-quiz flow.
// It does NOT duplicate the intake: "Começar" simply navigates to /nexis-quiz.
export function NexisAssistWidget() {
  const { lc } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); toggleRef.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const start = () => { setOpen(false); navigate("/nexis-quiz"); };

  return (
    <div className="fixed bottom-[84px] right-5 z-50 flex flex-col items-end" data-testid="nexis-assist-widget">
      {open && (
        <div
          id="nexis-assist-card"
          role="dialog"
          aria-label="NEXIS Assist"
          data-testid="nexis-assist-card"
          className="mb-3 w-[320px] max-w-[calc(100vw-2.5rem)] max-h-[calc(100dvh-160px)] overflow-y-auto overscroll-contain rounded-2xl border border-white/10 bg-[#041526] text-white shadow-[0_24px_60px_rgba(0,0,0,0.45)] origin-bottom-right"
          style={{ animation: "nexisPop 180ms ease-out" }}
        >
          <div className="relative p-5">
            <button
              type="button"
              ref={closeRef}
              onClick={() => { setOpen(false); toggleRef.current?.focus(); }}
              data-testid="nexis-assist-close"
              aria-label={lc({ pt: "Fechar", en: "Close" })}
              className="absolute right-3 top-3 rounded-md p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#f3bf57]/40 bg-[#f3bf57]/10">
                <img src="/logo-mark.png" alt="" width="26" height="26" className="h-[26px] w-[26px] object-contain" />
              </span>
              <div className="leading-tight">
                <div className="font-display text-lg font-extrabold">NEXIS Assist</div>
                <div className="text-[11px] font-semibold tracking-wide text-[#f3bf57]">Virtual Sourcing Assistant</div>
              </div>
            </div>

            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/65">Powered by BSGOTECH</div>

            <p className="mt-4 text-sm leading-relaxed text-white/85">
              {lc({
                pt: "Diga-nos o que a sua empresa precisa. O NEXIS interpreta a necessidade e encaminha o pedido para a capacidade certa — energia, água, modular, mobilidade, sourcing e mais.",
                en: "Tell us what your business needs. NEXIS interprets the need and routes the request to the right capability — energy, water, modular, mobility, sourcing and more.",
              })}
            </p>

            <button
              type="button"
              onClick={start}
              data-testid="nexis-assist-start"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#f3bf57] px-5 py-3 text-sm font-bold text-[#07192b] transition-colors hover:bg-[#ffd477]"
            >
              {lc({ pt: "Começar", en: "Get started" })} <ArrowRight size={15} />
            </button>

            <div className="mt-4 flex items-center gap-2 border-t border-white/10 pt-3 text-[11px] font-semibold uppercase tracking-wide text-white/70">
              <ShieldCheck size={13} className="text-[#f3bf57]" />
              NEXIS assists. Human approves.
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        ref={toggleRef}
        onClick={() => setOpen((v) => !v)}
        data-testid="nexis-assist-toggle"
        aria-label="NEXIS Assist"
        aria-expanded={open}
        aria-controls="nexis-assist-card"
        className="group relative flex h-12 w-12 items-center justify-center rounded-full border border-[#f3bf57]/40 bg-[#041526] shadow-[0_10px_30px_rgba(4,21,38,0.45)] transition-transform duration-200 hover:scale-105"
      >
        {!open && <span className="absolute inset-0 rounded-full bg-[#f3bf57]/30" style={{ animation: "nexisPulse 2.4s ease-out infinite" }} />}
        {open ? <X size={20} className="relative text-white" /> : <Sparkles size={20} className="relative text-[#f3bf57]" />}
      </button>

      <style>{`
        @keyframes nexisPulse { 0% { transform: scale(1); opacity: .6 } 70% { transform: scale(1.8); opacity: 0 } 100% { opacity: 0 } }
        @keyframes nexisPop { from { opacity: 0; transform: translateY(8px) scale(.96) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
    </div>
  );
}
